// Adapted from https://beui.dev/r/card; MIT notice in LICENSE.txt.
import { animate, isDragActive, motion, useMotionValue, useReducedMotion } from "motion/react";
import { useEffect, useRef, type PointerEvent, type ReactNode } from "react";

const PRESS_SPRING = { type: "spring", stiffness: 500, damping: 30 } as const;
export const CARD_TARGET_CLASS = "after:absolute after:inset-0";

/** BeUI's single-target card: its child button owns keyboard focus and semantics.
 * Local brand classes replace upstream theme/Tailwind 4 tokens. */
export function Card({ children }: { children: ReactNode }) {
  const reduce = useReducedMotion();
  const scale = useMotionValue(1);
  const releaseRef = useRef<(() => void) | null>(null);
  useEffect(() => () => releaseRef.current?.(), []);

  function press(event: PointerEvent<HTMLDivElement>) {
    if (reduce || releaseRef.current || !event.isPrimary || event.button !== 0 || isDragActive()) return;
    const options = { capture: true };
    const release = () => {
      window.removeEventListener("pointerup", release, options);
      window.removeEventListener("pointercancel", release, options);
      window.removeEventListener("pointermove", releaseOnDrag);
      releaseRef.current = null;
      animate(scale, 1, PRESS_SPRING);
    };
    const releaseOnDrag = () => { if (isDragActive()) release(); };
    window.addEventListener("pointerup", release, options);
    window.addEventListener("pointercancel", release, options);
    window.addEventListener("pointermove", releaseOnDrag);
    releaseRef.current = release;
    animate(scale, 0.985, PRESS_SPRING);
  }

  return <motion.div data-slot="card" style={{ scale: reduce ? 1 : scale }} onPointerDown={press}
    className="legal-policy-card relative flex flex-col rounded-2xl border border-line bg-white text-navy p-7 shadow-card">
    {children}
  </motion.div>;
}
